import { WelcomeCertificate } from "@/components/WelcomeCertificate";
import { PasskeyPrompt } from "@/components/Passkeys";
import { Link, useNavigate } from "react-router-dom";
import { PendingRatings } from "@/components/DoctorReviews";
import { CalendarPlus, ArrowRight, FileText, FolderHeart, Bell, Wallet, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { PageHead, StatusBadge, EmptyState } from "@/components/common";
import DoctorDirectory from "@/components/DoctorDirectory";
import { FullLoader } from "@/context/AuthContext";
import { PatientIdCard } from "@/pages/patient/PatientRecords";
import { fmtDT, fmtDate, inr, label, useApi, BACKEND, withAct } from "@/lib/api";

function FollowUps() {
  const { data } = useApi("/follow-ups");
  const nav = useNavigate();
  const [all, setAll] = useState(false);
  const pending = (data || []).filter((f) => f.status === "pending");
  if (!pending.length) return null;
  return (
    <div className="mb-6 space-y-2" data-testid="pending-followups">
      {pending.length > 2 && <button className="text-xs text-[#2412B5]" onClick={() => setAll(!all)} data-testid="followups-toggle">{all ? "Show fewer" : `Show all ${pending.length} free follow-ups`}</button>}
      {pending.slice(0, all ? undefined : 2).map((f) => (
        <div key={f.id} className="surface p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-[#086B2D]/40">
          <p className="text-sm"><b>Free follow-up</b> with {f.doctor_name} – book before {fmtDT(f.expires_at)} IST.</p>
          <Button size="sm" className="rounded-full" onClick={() => nav(`/patient/book/${f.doctor_id}?followup=${f.id}`)} data-testid={`book-followup-${f.id}`}>Book free follow-up</Button>
        </div>))}
    </div>
  );
}

export const ApptRow = ({ a, to }) => (
  <Link to={to} className="flex items-center justify-between gap-4 py-3 border-b border-[#E2E8ED] last:border-0 hover:bg-[#F7F9FA] -mx-2 px-2 rounded-lg" data-testid={`appt-row-${a.id}`}>
    <div className="min-w-0">
      <p className="text-sm font-medium truncate">{a.doctor_name}</p>
      <p className="text-xs muted">{fmtDT(a.start_at)} · <span className="capitalize">{a.mode}</span> · {a.ref}</p>
    </div>
    <StatusBadge status={a.status} />
  </Link>
);

const Card = ({ title, icon: Icon, to, children, testId }) => (
  <section className="surface p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold flex items-center gap-2"><Icon className="h-4 w-4 text-[#086477]" />{title}</h3>
      {to && <Link to={to} className="text-xs text-[#2412B5]">View all</Link>}
    </div>
    {children}
  </section>
);

export function PatientHome() {
  const { data, loading } = useApi("/patient/dashboard");
  const nav = useNavigate();
  if (loading || !data) return <FullLoader />;
  const next = data.upcoming[0];
  return (
    <div>
      <PatientIdCard />
      <PageHead eyebrow="Patient" title={`Namaste, ${data.name.split(" ")[0]}`} sub="Here's your health at a glance." />
      <PasskeyPrompt />
      <WelcomeCertificate />
      <FollowUps />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 surface p-6 bg-[#2412B5] border-[#2412B5] text-white fade-up" data-testid="upcoming-consultation-card">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">Upcoming consultation</p>
          {next ? (
            <>
              <p className="mt-3 text-xl font-semibold">{next.doctor_name}</p>
              <p className="text-white/80 text-sm">{next.specialty} · {fmtDT(next.start_at)} IST · <span className="capitalize">{next.mode}</span></p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button className="rounded-full bg-white text-[#2412B5] hover:bg-white/90" onClick={() => nav(`/patient/appointments/${next.id}`)} data-testid="upcoming-view-btn">
                  {next.status === "pending_payment" ? "Complete payment" : "View details"}</Button>
                {["confirmed", "in_progress"].includes(next.status) && <Button variant="outline" className="rounded-full bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white" onClick={() => nav(`/consultation/${next.id}`)} data-testid="upcoming-join-btn"><Video className="h-4 w-4 mr-2" />Join room</Button>}
              </div>
            </>
          ) : <p className="mt-3 text-white/80">No upcoming consultations.</p>}
        </div>
        <button onClick={() => nav("/patient/find-doctor")} className="surface p-6 text-left hover:border-[#086B2D] transition-colors fade-up delay-1" data-testid="patient-book-appointment-btn">
          <CalendarPlus className="h-6 w-6 text-[#086B2D]" />
          <p className="mt-4 font-semibold">Book Consultation</p>
          <p className="text-sm muted">Find a verified doctor and pick a slot.</p>
          <ArrowRight className="mt-4 h-4 w-4 text-[#086B2D]" />
        </button>
      </div>
      <div className="mt-4 surface p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="profile-completion">
        <div className="flex-1"><p className="text-sm font-medium">Profile {data.completion}% complete</p><Progress value={data.completion} className="mt-2 h-1.5" /></div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => nav("/patient/profile")}>Update profile</Button>
      </div>
      {data.pending_payments > 0 && <p className="mt-4 text-sm text-[#9A5B00] flex items-center gap-2" data-testid="pending-payment-alert"><Wallet className="h-4 w-4" />{data.pending_payments} booking(s) awaiting payment.</p>}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <Card title="Recent consultations" icon={CalendarPlus} to="/patient/appointments" testId="recent-consultations">
          {data.recent.length ? data.recent.map((a) => <ApptRow key={a.id} a={a} to={`/patient/appointments/${a.id}`} />) : <p className="text-sm muted">None yet.</p>}
        </Card>
        <Card title="Prescriptions" icon={FileText} to="/patient/records" testId="recent-prescriptions">
          {data.prescriptions.length ? data.prescriptions.map((p) => (
            <a key={p.id} href={withAct(`${BACKEND}/api/prescriptions/${p.id}/pdf`)} target="_blank" rel="noreferrer" className="flex justify-between py-2 text-sm border-b last:border-0 border-[#E2E8ED]">
              <span>{p.doctor?.name}</span><span className="muted">{fmtDate(p.finalized_at)}</span></a>
          )) : <p className="text-sm muted">Prescriptions from your consultations will appear here.</p>}
        </Card>
        <Card title="Uploaded reports" icon={FolderHeart} to="/patient/records" testId="recent-reports">
          {data.records.length ? data.records.map((r) => <p key={r.id} className="py-2 text-sm border-b last:border-0 border-[#E2E8ED] flex justify-between"><span className="truncate">{r.filename}</span><span className="muted capitalize shrink-0 ml-2">{label(r.category)}</span></p>) : <p className="text-sm muted">No reports uploaded.</p>}
        </Card>
        <Card title="Follow-up reminders" icon={Bell} testId="follow-ups">
          {data.follow_ups.length ? data.follow_ups.map((f) => <p key={f.id} className="py-2 text-sm flex justify-between"><span>{f.doctor_name}</span><span className="text-[#086B2D]">Due {fmtDate(f.due_date)}</span></p>) : <p className="text-sm muted">No follow-ups due.</p>}
        </Card>
      </div>
    </div>
  );
}

export function PatientFindDoctor() {
  return (
    <div>
      <PageHead eyebrow="Find doctor" title="Book a consultation" sub="Verified doctors only. Times are in IST." />
      <DoctorDirectory scope={{ category: "clinician" }} bookPath={(id) => `/patient/book/${id}`} />
    </div>
  );
}

export function PatientAppointments() {
  const [scope, setScope] = useState("upcoming");
  const { data, loading } = useApi(`/appointments?scope=${scope}`);
  return (
    <div>
      <PageHead eyebrow="Appointments" title="Your consultations" right={<Button className="rounded-full" onClick={() => window.location.assign("/patient/find-doctor")} data-testid="appointments-book-btn">Book new</Button>} />
      <PendingRatings />
      <div className="mt-4" />
      <Tabs value={scope} onValueChange={setScope}><TabsList><TabsTrigger value="upcoming" data-testid="appts-tab-upcoming">Upcoming</TabsTrigger><TabsTrigger value="past" data-testid="appts-tab-past">Past</TabsTrigger><TabsTrigger value="all" data-testid="appts-tab-all">All</TabsTrigger></TabsList></Tabs>
      <div className="surface p-5 mt-4">
        {loading ? <FullLoader /> : data?.length ? data.map((a) => <ApptRow key={a.id} a={a} to={`/patient/appointments/${a.id}`} />) : <EmptyState title="No consultations here" text="Book a consultation to get started." />}
      </div>
    </div>
  );
}

export function PatientPayments() {
  const { data, loading } = useApi("/payments");
  if (loading || !data) return <FullLoader />;
  return (
    <div>
      <PageHead eyebrow="Billing" title="Payments & receipts" />
      <div className="surface overflow-x-auto" data-testid="payments-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F9FA] text-left text-xs muted"><tr><th className="p-3">Receipt</th><th className="p-3">Consultation</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Date</th></tr></thead>
          <tbody>
            {data.payments.map((p) => (
              <tr key={p.id} className="border-t border-[#E2E8ED]"><td className="p-3">{p.receipt_no}<p className="text-[11px] muted">{p.provider_payment_id || p.order_id}</p></td><td className="p-3">{p.appointment?.doctor_name}<p className="text-[11px] muted">{p.appointment?.ref}</p></td>
                <td className="p-3">{inr(p.amount)}</td><td className="p-3"><StatusBadge status={p.status} /></td><td className="p-3">{fmtDT(p.captured_at || p.created_at)}</td></tr>
            ))}
          </tbody>
        </table>
        {data.payments.length === 0 && <div className="p-6"><EmptyState title="No payments yet" /></div>}
      </div>
      {data.refunds.length > 0 && (
        <div className="surface p-5 mt-6" data-testid="refunds-list"><h3 className="font-semibold mb-2">Refunds</h3>
          {data.refunds.map((r) => <div key={r.id} className="flex justify-between py-2 text-sm border-t border-[#E2E8ED]"><span>{inr(r.amount)} · {fmtDT(r.created_at)}</span><StatusBadge status={r.status} /></div>)}
        </div>
      )}
    </div>
  );
}
